import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Save, User, Camera, CreditCard } from 'lucide-react';
import FileUpload from './FileUpload';

const API = process.env.REACT_APP_BACKEND_URL;

const computeLengthOfService = (startDate) => {
  if (!startDate) return null;
  const start = new Date(startDate);
  if (isNaN(start.getTime())) return null;
  const now = new Date();
  if (start > now) return null;
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();
  if (days < 0) {
    months -= 1;
    // Use previous month's day count
    const prev = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
    days += prev;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  const parts = [];
  if (years > 0) parts.push(`${years} yr${years > 1 ? 's' : ''}`);
  if (months > 0) parts.push(`${months} mo${months > 1 ? 's' : ''}`);
  if (parts.length === 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  return parts.join(' ');
};

const TEXT_FIELDS = [
  { name: 'name', label: 'Full Name', type: 'text', placeholder: 'John Doe' },
  { name: 'phone', label: 'Phone', type: 'tel', placeholder: '+1234567890' },
  { name: 'department', label: 'Department', type: 'text', placeholder: 'Mathematics' },
  { name: 'employee_id', label: 'Employee ID', type: 'text', placeholder: 'TCH001' },
  { name: 'job_role', label: 'Role/Position', type: 'text', placeholder: 'Senior Teacher' },
  { name: 'start_work_date', label: 'Start Work Date', type: 'date' },
  { name: 'date_of_birth', label: 'Date of Birth', type: 'date' },
  { name: 'identification_number', label: 'ID Number', type: 'text', placeholder: 'Passport/National ID' },
  { name: 'bank_name', label: 'Bank Name', type: 'text', placeholder: 'Bank of America' },
  { name: 'bank_account_number', label: 'Bank Account Number', type: 'text', placeholder: '1234567890' },
  { name: 'emergency_contact_name', label: 'Emergency Contact Name', type: 'text', placeholder: 'Jane Doe' },
  { name: 'emergency_contact_phone', label: 'Emergency Contact Phone', type: 'tel', placeholder: '+1234567890' },
];

const ADMIN_ONLY_FIELDS = [
  { name: 'email', label: 'Email', type: 'email', placeholder: 'user@example.com' },
  { name: 'annual_leave_allowance', label: 'Pro rate Allowance (days)', type: 'number', placeholder: '14', step: '0.5' },
  { name: 'sick_leave_allowance', label: 'Sick Leave Allowance (days)', type: 'number', placeholder: '8', step: '0.5' },
];

const SELECT_FIELDS = [
  {
    name: 'sex',
    label: 'Sex',
    options: [
      { value: 'male', label: 'Male' },
      { value: 'female', label: 'Female' },
      { value: 'other', label: 'Other' },
      { value: 'prefer_not_to_say', label: 'Prefer not to say' },
    ],
  },
  {
    name: 'race',
    label: 'Race / Ethnicity',
    options: [
      { value: 'malay', label: 'Malay' },
      { value: 'chinese', label: 'Chinese' },
      { value: 'indian', label: 'Indian' },
      { value: 'bumiputera', label: 'Bumiputera' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    name: 'religion',
    label: 'Religion',
    options: [
      { value: 'islam', label: 'Islam' },
      { value: 'christianity', label: 'Christianity' },
      { value: 'buddhism', label: 'Buddhism' },
      { value: 'hinduism', label: 'Hinduism' },
      { value: 'sikhism', label: 'Sikhism' },
      { value: 'none', label: 'None' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    name: 'diet',
    label: 'Diet Preference',
    options: [
      { value: 'no_restrictions', label: 'No Restrictions' },
      { value: 'vegetarian', label: 'Vegetarian' },
      { value: 'halal', label: 'Halal' },
      { value: 'other', label: 'Other (see notes)' },
    ],
  },
  {
    name: 'nationality',
    label: 'Nationality',
    options: [
      { value: 'malaysia', label: 'Malaysia' },
      { value: 'singapore', label: 'Singapore' },
      { value: 'brunei', label: 'Brunei' },
      { value: 'indonesia', label: 'Indonesia' },
      { value: 'thailand', label: 'Thailand' },
      { value: 'other', label: 'Other' },
    ],
  },
];

const TEXTAREA_FIELDS = [
  { name: 'address', label: 'Address', placeholder: 'Full address...' },
  { name: 'education_background', label: 'Education Background', placeholder: 'MSc in Mathematics from...' },
  { name: 'health_info', label: 'Health Information', placeholder: 'Allergies, medical conditions, dietary notes, etc.' },
];

const REQUIRED_TEACHER_FIELDS = [
  { name: 'department', label: 'Department' },
  { name: 'employee_id', label: 'Employee ID' },
  { name: 'job_role', label: 'Role/Position' },
  { name: 'start_work_date', label: 'Start Work Date' },
  { name: 'identification_number', label: 'ID Number' },
  { name: 'bank_name', label: 'Bank Name' },
  { name: 'bank_account_number', label: 'Bank Account Number' },
  { name: 'emergency_contact_name', label: 'Emergency Contact Name' },
  { name: 'emergency_contact_phone', label: 'Emergency Contact Phone' },
];

const ProfileForm = ({ userId = null, viewOnly = false, onSaved = null }) => {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [facePhotoBlobUrl, setFacePhotoBlobUrl] = useState(null);

  const endpoint = userId ? `${API}/api/profile/${userId}` : `${API}/api/profile`;

  const loadFacePhoto = useCallback(async (fileId) => {
    try {
      const res = await axios.get(`${API}/api/files/${fileId}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      setFacePhotoBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (error) {
      // Silently fail — profile picture is optional
      console.debug('Face photo load skipped', error);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(endpoint, { withCredentials: true });
      setProfile(data);
    } catch (error) {
      console.error('Failed to load profile', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    fetchProfile();
    return () => {
      if (facePhotoBlobUrl) URL.revokeObjectURL(facePhotoBlobUrl);
    };
    // We intentionally do not include facePhotoBlobUrl — its cleanup uses the latest value via closure,
    // and including it would re-run the fetch on every blob URL swap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProfile]);

  useEffect(() => {
    if (profile.face_photo_id) {
      loadFacePhoto(profile.face_photo_id);
    } else {
      setFacePhotoBlobUrl(null);
    }
  }, [profile.face_photo_id, loadFacePhoto]);

  const handleChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoUploaded = (field, fileData) => {
    setProfile((prev) => ({ ...prev, [field]: fileData ? fileData.id : '' }));
  };

  const missingTeacherFields = profile.role === 'teacher'
    ? REQUIRED_TEACHER_FIELDS.filter((f) => !profile[f.name] || profile[f.name].toString().trim() === '')
    : [];
  const isTeacherProfileIncomplete = missingTeacherFields.length > 0;
  const isTeacherSelfEdit = !userId && profile.role === 'teacher';
  const saveDisabled = saving || (isTeacherSelfEdit && isTeacherProfileIncomplete);

  const handleSave = async (e) => {
    e.preventDefault();
    if (isTeacherSelfEdit && isTeacherProfileIncomplete) {
      toast.error('Please complete all required teacher profile fields before saving.');
      return;
    }
    setSaving(true);
    try {
      const allFields = [
        ...TEXT_FIELDS.map((f) => f.name),
        ...SELECT_FIELDS.map((f) => f.name),
        ...TEXTAREA_FIELDS.map((f) => f.name),
        'face_photo_id',
        'id_card_front_id',
        'id_card_back_id',
      ];
      // Admin editing another user's profile can also set leave allowances
      if (userId) {
        allFields.push('annual_leave_allowance');
        allFields.push('sick_leave_allowance');
      }
      const payload = {};
      allFields.forEach((name) => {
        if (profile[name] !== undefined) payload[name] = profile[name] === '' ? '' : profile[name];
      });
      const { data } = await axios.put(endpoint, payload, { withCredentials: true });
      setProfile(data);
      toast.success('Profile updated successfully');
      if (onSaved) onSaved(data);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <Card className="bg-white border-slate-200" data-testid="profile-form-card">
      <CardHeader>
        <div className="flex items-center gap-4">
          {/* Profile picture avatar */}
          <div className="relative">
            {facePhotoBlobUrl ? (
              <img
                src={facePhotoBlobUrl}
                alt={profile.name || 'Profile'}
                className="w-16 h-16 rounded-full object-cover border-2 border-blue-100"
                data-testid="profile-picture"
              />
            ) : (
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-blue-600" />
              </div>
            )}
          </div>
          <div>
            <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {viewOnly ? 'Profile Information' : 'Personal Profile'}
            </CardTitle>
            <CardDescription>
              {viewOnly ? 'View and update profile details' : 'Fill in your personal information'}
            </CardDescription>
            {profile.start_work_date && computeLengthOfService(profile.start_work_date) && (
              <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 border border-blue-200" data-testid="length-of-service">
                <span className="text-slate-500">Length of Service:</span>
                <span className="font-semibold">{computeLengthOfService(profile.start_work_date)}</span>
                <span className="text-slate-400">• Since {new Date(profile.start_work_date).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {profile.role === 'teacher' && missingTeacherFields.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-4">
            <p className="text-sm font-semibold text-amber-800">Teacher profile incomplete</p>
            <p className="text-sm text-amber-700">Please fill in the following fields to keep your profile complete:</p>
            <ul className="list-disc list-inside mt-2 text-sm text-amber-800">
              {missingTeacherFields.map((field) => (
                <li key={field.name}>{field.label}</li>
              ))}
            </ul>
          </div>
        )}
        <form onSubmit={handleSave} className="space-y-6" data-testid="profile-form">
          {/* Photo uploads section */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">
              Photos & Identity Documents
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 border border-slate-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Camera className="w-4 h-4 text-blue-600" />
                  <Label className="text-sm font-medium text-slate-700">Face Photo (Profile Picture)</Label>
                </div>
                <FileUpload
                  testIdPrefix="face-photo"
                  currentFileId={profile.face_photo_id}
                  currentFileName={profile.face_photo_id ? 'Face Photo' : null}
                  onFileUploaded={(f) => handlePhotoUploaded('face_photo_id', f)}
                />
              </div>
              <div className="p-4 border border-slate-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <Label className="text-sm font-medium text-slate-700">ID Card (Front)</Label>
                </div>
                <FileUpload
                  testIdPrefix="id-front"
                  currentFileId={profile.id_card_front_id}
                  currentFileName={profile.id_card_front_id ? 'ID Card Front' : null}
                  onFileUploaded={(f) => handlePhotoUploaded('id_card_front_id', f)}
                />
              </div>
              <div className="p-4 border border-slate-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <Label className="text-sm font-medium text-slate-700">ID Card (Back)</Label>
                </div>
                <FileUpload
                  testIdPrefix="id-back"
                  currentFileId={profile.id_card_back_id}
                  currentFileName={profile.id_card_back_id ? 'ID Card Back' : null}
                  onFileUploaded={(f) => handlePhotoUploaded('id_card_back_id', f)}
                />
              </div>
            </div>
          </div>

          {/* Read-only email */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Email (read-only)</Label>
            <Input value={profile.email || ''} disabled className="border-slate-300 bg-slate-50 mt-1" data-testid="profile-email-input" />
          </div>

          {/* Text fields */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Personal Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {TEXT_FIELDS.map((field) => (
                <div key={field.name}>
                  <Label htmlFor={field.name} className="text-sm font-medium text-slate-700">
                    {field.label}
                  </Label>
                  <Input
                    id={field.name}
                    type={field.type}
                    value={profile[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    className="border-slate-300 mt-1"
                    data-testid={`profile-${field.name}-input`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Admin-only fields */}
          {userId && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Admin Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ADMIN_ONLY_FIELDS.map((field) => (
                  <div key={field.name}>
                    <Label htmlFor={field.name} className="text-sm font-medium text-slate-700">
                      {field.label}
                    </Label>
                    <Input
                      id={field.name}
                      type={field.type}
                      step={field.step}
                      value={profile[field.name] ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const val = field.type === 'number'
                          ? (raw === '' ? '' : parseFloat(raw))
                          : raw;
                        handleChange(field.name, val);
                      }}
                      placeholder={field.placeholder}
                      className="border-slate-300 mt-1"
                      data-testid={`profile-${field.name}-input`}
                    />
                    {field.name === 'annual_leave_allowance' && profile.annual_leave_used !== undefined && (
                      <p className="text-xs text-slate-500 mt-1">Used so far: {profile.annual_leave_used} days</p>
                    )}
                    {field.name === 'email' && (
                      <p className="text-xs text-amber-600 mt-1">
                        Changing this address will affect the user's login — inform them of the change.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Demographic selects */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">
              Demographics & Preferences
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {SELECT_FIELDS.map((field) => (
                <div key={field.name}>
                  <Label htmlFor={field.name} className="text-sm font-medium text-slate-700">
                    {field.label}
                  </Label>
                  <Select
                    value={profile[field.name] || ''}
                    onValueChange={(v) => handleChange(field.name, v)}
                  >
                    <SelectTrigger className="mt-1" data-testid={`profile-${field.name}-select`}>
                      <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value} data-testid={`profile-${field.name}-option-${opt.value}`}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          {/* Textareas */}
          <div className="space-y-4">
            {TEXTAREA_FIELDS.map((field) => (
              <div key={field.name}>
                <Label htmlFor={field.name} className="text-sm font-medium text-slate-700">
                  {field.label}
                </Label>
                <Textarea
                  id={field.name}
                  value={profile[field.name] || ''}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  className="border-slate-300 mt-1"
                  rows={3}
                  data-testid={`profile-${field.name}-input`}
                />
              </div>
            ))}
          </div>

          <div className="flex flex-col items-end pt-4 border-t border-slate-200">
            <Button
              type="submit"
              disabled={saveDisabled}
              className="bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
              data-testid="save-profile-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            {isTeacherSelfEdit && isTeacherProfileIncomplete && (
              <p className="mt-2 text-xs text-amber-700">
                Complete the required teacher fields above to enable saving this profile.
              </p>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ProfileForm;
